import { type RPPDomain } from '@retailer-platform/dashboard/core'
import { NavEntryPointV3 } from '@retailer-platform/dashboard/routing'
import { merchandisingAccessControl } from './access-control/merchandisingAccess.configuration'
import { merchandisingScopedRouter } from './routing/merchandisingScopedRouter'
import { type DomainMessages } from './utils/domain/intl'
import { routes } from './routing/routes'
import EN_US from './translations/en-US.json'
import { type DomainAccessControlConfig } from './utils/domain/accessControl'

const definition: RPPDomain<typeof routes, DomainMessages, DomainAccessControlConfig> = {
  id: 'merchandising',
  totem: {
    // The default IPP tool of all routes in this domain - should match the Navigation Entry in IPP UI.
    // Individual routes can override this by specifying a `tool` prop on their DomainRoute component
    tool: 'merchandising-dashboard-tool',
  },
  routes,
  translations: { EN_US },
  lazyTranslations: {
    ES_ES: () => import('./translations/es-ES.json'),
    FR_FR: () => import('./translations/fr-FR.json'),
  },
  routers: {
    scoped: [merchandisingScopedRouter],
  },
  navigationEntries: [
    {
      // Reuse the same warehouse-scoped `merchandising-hero-banners-list` route
      // as v2 (identical behavior → reuse route name, no flavored clone). The
      // earlier `-storefront` variant added a `storeConfigFilter` that broke the
      // page because Hero Banners is warehouse-scoped, not store-config-scoped.
      attachToV3: NavEntryPointV3.Storefront,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage hero banners for storefront homepage',
          labelId: 'merchandising.hero-banners.title',
          route: 'merchandising-hero-banners-list',
          subRoutes: ['merchandising-hero-banners-create'],
          accessControl: merchandisingAccessControl,
          positionNavV3: 5,
          dividerBefore: true,
        },
      ],
    },
    {
      // Hero Banners is the only storefront tool with no SFP-flavored twin, so
      // it would become unreachable for combined SF+SFP retailers once the SF L1
      // is suppressed in favor of SFP. Re-attach the same route under the SFP L1,
      // tier-gated to `storefront` so SFP-only retailers (who have no Storefront
      // site) don't see it.
      attachToV3: NavEntryPointV3.StorefrontPro,
      subNavItems: [
        {
          type: 'item',
          llmDescription: 'Create and manage hero banners for storefront homepage',
          labelId: 'merchandising.hero-banners.title',
          route: 'merchandising-hero-banners-list',
          subRoutes: ['merchandising-hero-banners-create'],
          accessControl: merchandisingAccessControl,
          positionNavV3: 6.5,
          requiredTierV3: 'storefront', // only for combined SF+SFP retailers
        },
      ],
    },
  ],
  adminNavigationEntries: [],
}

export default definition
